import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Kaart, Knop, Leeg, Modaal, PaginaKop, SkeletVlak, Veld, useToast } from '../components/ui';
import { useAuth } from '../lib/auth';
import { usePoule } from '../lib/poule';
import { fmtDatum } from '../lib/format';
import {
  useBeantwoordUitnodiging,
  useMaakPoule,
  usePouleLeden,
  usePoules,
  useUitnodigingen,
  useZetFavoriet
} from '../lib/queries';

export default function Poules() {
  const { profile, mag } = useAuth();
  const poules = usePoules();
  const leden = usePouleLeden();
  const uitnodigingen = useUitnodigingen();
  const beantwoorden = useBeantwoordUitnodiging();
  const favoriet = useZetFavoriet();
  const { pouleId, kies } = usePoule();
  const meld = useToast();
  const [nieuw, setNieuw] = useState(false);

  const mijnLidmaatschappen = new Map(
    (leden.data ?? []).filter((l) => l.profielId === profile?.id).map((l) => [l.pouleId, l.rol])
  );
  const open = (uitnodigingen.data ?? []).filter((u) => u.profielId === profile?.id && u.status === 'open');
  /* favorieten bovenaan: dat is meestal de competitie waar je in zit */
  const favorieten = new Set(
    (leden.data ?? []).filter((l) => l.profielId === profile?.id && l.favoriet).map((l) => l.pouleId)
  );
  const mijnPoules = (poules.data ?? [])
    .filter((p) => mijnLidmaatschappen.has(p.id))
    .sort((a, b) => (favorieten.has(b.id) ? 1 : 0) - (favorieten.has(a.id) ? 1 : 0) || a.naam.localeCompare(b.naam));

  const ledenTal = (pouleId: string) => (leden.data ?? []).filter((l) => l.pouleId === pouleId).length;

  return (
    <>
      <PaginaKop
        titel="Poules"
        onder="Een poule is een competitie op zich: eigen ranglijst, eigen seizoenen, eigen toernooien. Je kunt er aan meerdere tegelijk meedoen."
        rechts={
          mag('actie.poule_aanmaken') ? (
            <Knop variant="primair" onClick={() => setNieuw(true)}>
              + Poule
            </Knop>
          ) : undefined
        }
      />

      {open.length ? (
        <div className="mb-3.5 border-2 border-krijt-900 bg-signaal/25 p-4">
          <p className="kop mb-3 text-[15px]">Je bent uitgenodigd</p>
          <div className="flex flex-col gap-2">
            {open.map((u) => {
              const poule = (poules.data ?? []).find((p) => p.id === u.pouleId);
              return (
                <div key={u.id} className="flex flex-wrap items-center gap-3 border border-krijt-300 bg-white px-3 py-2.5">
                  <span className="font-semibold">{poule?.naam ?? 'Een poule'}</span>
                  {poule?.prijs ? <span className="text-sm text-krijt-500">speelt om: {poule.prijs}</span> : null}
                  <span className="ml-auto flex gap-2">
                    <Knop
                      klein
                      variant="primair"
                      onClick={async () => {
                        try {
                          await beantwoorden.mutateAsync({ id: u.id, akkoord: true });
                          meld('Je doet mee.');
                        } catch (err) {
                          meld(err instanceof Error ? err.message : 'Mislukt.', true);
                        }
                      }}
                    >
                      Meedoen
                    </Knop>
                    <Knop
                      klein
                      onClick={async () => {
                        try {
                          await beantwoorden.mutateAsync({ id: u.id, akkoord: false });
                          meld('Afgeslagen.');
                        } catch (err) {
                          meld(err instanceof Error ? err.message : 'Mislukt.', true);
                        }
                      }}
                    >
                      Nee, bedankt
                    </Knop>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {poules.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <SkeletVlak hoogte="h-56" />
          <SkeletVlak hoogte="h-56" />
          <SkeletVlak hoogte="h-56" />
        </div>
      ) : mijnPoules.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {mijnPoules.map((p) => (
            /* Vaste minimumhoogte en de knoppen op de bodem: zo staan de tegels
               in een rij netjes gelijk, ook als de een een omschrijving heeft
               en de ander niet. */
            <Link
              key={p.id}
              to={`/poules/${p.id}`}
              className="rand-hard flex min-h-[236px] flex-col bg-white p-5 transition hover:bg-krijt-50"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="kop text-[21px] leading-tight">
                  {favorieten.has(p.id) ? (
                    <span className="mr-1.5 text-goud" title="favoriet">
                      ★
                    </span>
                  ) : null}
                  {p.naam}
                </h2>
                {mijnLidmaatschappen.get(p.id) === 'beheerder' ? (
                  <span className="opschrift shrink-0 border border-veld-300 bg-veld-50 px-1.5 py-0.5 text-veld-800">
                    beheerder
                  </span>
                ) : null}
              </div>

              {p.omschrijving ? (
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-krijt-600">{p.omschrijving}</p>
              ) : null}

              {p.prijs ? (
                <p className="mt-3 border-l-2 border-signaal pl-2.5 text-sm">
                  <span className="opschrift block text-krijt-500">Speelt om</span>
                  <span className="font-semibold">{p.prijs}</span>
                </p>
              ) : null}

              {/* Alles hieronder staat op de bodem van de tegel. */}
              <div className="mt-auto pt-4">
                <div className="flex items-end gap-5 border-t border-krijt-200 pt-3">
                  <span>
                    <span className="kop nums block text-[20px] leading-none">{ledenTal(p.id)}</span>
                    <span className="opschrift text-krijt-500">deelnemers</span>
                  </span>
                  <span>
                    <span className="block text-[13px] font-semibold leading-none">{fmtDatum(p.createdAt)}</span>
                    <span className="opschrift text-krijt-500">bestaat sinds</span>
                  </span>
                  {p.id === pouleId ? (
                    <span className="ml-auto self-center rounded-full bg-veld-50 px-2 py-0.5 text-[11px] font-semibold text-veld-800">
                      je bekijkt deze
                    </span>
                  ) : null}
                </div>

                <span className="mt-3 flex gap-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      void favoriet.mutateAsync({
                        pouleId: p.id,
                        favoriet: !favorieten.has(p.id)
                      });
                    }}
                    className="border border-krijt-300 px-2.5 py-1.5 text-xs transition hover:bg-krijt-100"
                  >
                    {favorieten.has(p.id) ? '★ favoriet' : '☆ favoriet maken'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      kies(p.id);
                      meld(`Je bekijkt nu ${p.naam}.`);
                    }}
                    className="border border-krijt-300 px-2.5 py-1.5 text-xs transition hover:bg-krijt-100"
                    disabled={p.id === pouleId}
                  >
                    Hierin spelen
                  </button>
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <Kaart>
          <Leeg
            tekst="Je zit nog in geen enkele poule. Maak er een aan, of laat je uitnodigen op je gebruikersnaam."
            ikoon="poule"
          />
        </Kaart>
      )}

      {nieuw ? <NieuwePouleModaal onSluit={() => setNieuw(false)} /> : null}
    </>
  );
}

function NieuwePouleModaal({ onSluit }: { onSluit: () => void }) {
  const { profile } = useAuth();
  const maken = useMaakPoule();
  const meld = useToast();
  const [naam, setNaam] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [prijs, setPrijs] = useState('');
  const [fout, setFout] = useState<string | null>(null);

  async function maak() {
    if (!naam.trim()) {
      setFout('Geef de poule een naam.');
      return;
    }
    if (!profile) return;
    try {
      await maken.mutateAsync({
        naam: naam.trim(),
        omschrijving: omschrijving.trim() || null,
        prijs: prijs.trim() || null,
        door: profile.id
      });
      meld('Poule aangemaakt. Je bent er de beheerder van.');
      onSluit();
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Aanmaken mislukte.');
    }
  }

  return (
    <Modaal
      titel="Nieuwe poule"
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop variant="primair" onClick={() => void maak()} disabled={maken.isPending}>
            {maken.isPending ? 'Bezig…' : 'Aanmaken'}
          </Knop>
        </>
      }
    >
      {fout ? <div className="mb-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{fout}</div> : null}

      <Veld label="Naam">
        <input className="veld" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="bijv. Kantinecup" />
      </Veld>

      <Veld label="Omschrijving">
        <input
          className="veld"
          value={omschrijving}
          onChange={(e) => setOmschrijving(e.target.value)}
          placeholder="Waar gaat het om?"
        />
      </Veld>

      <Veld label="Prijs" help="Wat wint de winnaar? Eeuwige roem mag ook.">
        <input
          className="veld"
          value={prijs}
          onChange={(e) => setPrijs(e.target.value)}
          placeholder="bijv. taart van de verliezer"
        />
      </Veld>

      <p className="text-xs text-krijt-500">
        Je wordt automatisch beheerder van deze poule en kunt daarna anderen uitnodigen op hun gebruikersnaam.
      </p>
    </Modaal>
  );
}
